import React, { useCallback, useMemo, useRef, useState } from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { Box, Button, Hidden, Typography } from '@mui/material';
import CmtCard from '@coremat/CmtCard';
import CmtCardHeader from '@coremat/CmtCard/CmtCardHeader';
import CmtCardContent from '@coremat/CmtCard/CmtCardContent';
import { useActiveCart, useCart, usePatchApproveCart, usePostCheckout } from 'services/Data/CartService';
import GridContainer from '@jumbo/components/GridContainer';
import GridItem from '@jumbo/components/GridItem';
import DeliveryCheckoutSection from './Components/DeliveryCheckoutSection';
import OrderInfoCheckoutSection from './Components/OrderInfoCheckoutSection';
import { useHistory } from 'react-router-dom';
import BaseModal from 'Components/BaseModal';
import SimpleReactValidator from 'simple-react-validator';
import { validatorConfig } from 'services/Validators/Validation';
import useToast from 'services/Hooks/useToast';
import useForceUpdate from 'services/Hooks/useForceUpdate';
import { useParams } from 'react-router-dom';
import { CartStatusEnumValue } from 'enums/CartStatusEnum';
import { useSelector } from 'react-redux';
import ContactInfoCheckoutSection from './Components/ContactInfoCheckoutSection';
import { homePage, IsImpersonationMode } from 'config/config';
import StorePageHeader from 'Components/StorePageHeader';

const useStyles = makeStyles(() => ({
  pageFull: {
    width: '100%',
  },
  noItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cartFooter: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  checkoutBtn: {
    marginTop: '1vh',
  },
  deliverySection: {
    position: 'sticky',
    top: '10vh',
  },
  card: {
    overflow: 'visible',
  },
}));

const Checkout = ({ fromHistory }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const forceUpdate = useForceUpdate();

  const { errorMessage } = useToast();
  const validator = useRef(new SimpleReactValidator(validatorConfig.getConfig(t)));

  const [showCheckoutConfirmed, setShowCheckoutConfirmed] = useState(false);
  const [showApprouveModal, setShowApprouveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const accountReducer = useSelector(state => state.AccountReducer);
  const dealerId = accountReducer?.profile?.dealer_Id;

  let { cartId } = useParams();

  // active cart is used in cart (it's the current cart being in use)
  // history cart is used in the history only
  const { data: activeCart, isLoading: gettingCart } = useActiveCart(
    accountReducer.isLoggedIn,
    true,
    IsImpersonationMode ? dealerId : null,
  );
  const { data: historyCart, isLoading: gettingCartHistory } = useCart(cartId);

  const cart = useMemo(() => (cartId ? historyCart : activeCart), [activeCart, historyCart, cartId]);

  const { mutate: checkout, isPending: isCheckingOut } = usePostCheckout();
  const { mutateAsync: changeStatut } = usePatchApproveCart();

  const backUrl = useMemo(() => (fromHistory ? '/dealer/profile/request-histories' : '/dealer/profile/awaiting-requests'), [
    fromHistory,
  ]);

  const handleCheckout = useCallback(() => {
    if (!validator.current.allValid()) {
      validator.current.showMessages();
      forceUpdate();

      errorMessage({
        message: 'contraint_errors',
      });
      return;
    }
    validator.current.hideMessages();

    checkout(cart);
    setShowCheckoutConfirmed(true);
  }, [cart, checkout, errorMessage, forceUpdate]);

  const handleCloseCancelCartModal = useCallback(() => {
    setShowCancelModal(false);
  }, [setShowCancelModal]);

  const handleCloseApproveCartModal = useCallback(() => {
    setShowApprouveModal(false);
  }, [setShowApprouveModal]);

  const handleCloseConfirmedCheckoutModal = useCallback(() => {
    setShowCheckoutConfirmed(false);
  }, [setShowCheckoutConfirmed]);

  const handleChangeStatus = useCallback(
    newStatus => {
      changeStatut({ ...cart, status: newStatus }).then(() => {
        history.push(backUrl);
      });
    },
    [cart, changeStatut, backUrl, history],
  );

  return (
    <>
      <Box sx={{ mb: 9 }}>
        <StorePageHeader title={t('checkout')} imgLeft backgroundColor="lightGray" borderRadius="0.5rem" />
      </Box>

      <Box className={classes.pageFull}>
        <BaseModal
          show={showCheckoutConfirmed}
          title={t('checkoutConfirmed')}
          message={t('checkoutConfirmedText')}
          closeBtnMessage={t('close')}
          onClose={handleCloseConfirmedCheckoutModal}
          actions={[
            {
              onClick: () => history.push(homePage),
              color: 'secondary',
              style: { color: 'black', borderRadius: 0 },
              variant: 'contained',
              message: t('goToHome'),
            },
          ]}
        />
        <BaseModal
          show={showApprouveModal}
          title={t('approveCart')}
          message={t('approveCartText')}
          closeBtnMessage={t('close')}
          onClose={handleCloseApproveCartModal}
          actions={[
            {
              onClick: () => {
                handleChangeStatus(CartStatusEnumValue.Accepted);
              },
              color: 'secondary',
              style: { color: 'black', borderRadius: 0 },
              variant: 'contained',
              message: t('approve'),
            },
          ]}
        />
        <BaseModal
          show={showCancelModal}
          title={t('cancelCart')}
          message={t('cancelCartText')}
          closeBtnMessage={t('close')}
          onClose={handleCloseCancelCartModal}
          actions={[
            {
              onClick: () => {
                handleChangeStatus(CartStatusEnumValue.Cancelled);
                history.push(backUrl);
              },
              color: 'error',
              style: { color: 'white' },
              variant: 'contained',
              message: t('cancel'),
            },
          ]}
        />
        <CmtCard className={classes.card}>
          <CmtCardHeader>
            {cartId && (
              <Button sx={{ borderRadius: 0 }} variant={'contained'} color="secondary" onClick={() => history.push(backUrl)}>
                <Typography
                  sx={{
                    color: 'black !important',
                    fontSize: 12,
                    fontWeight: 700,
                    paddingRight: 10,
                    paddingLeft: 10,
                    paddingTop: 2,
                    paddingBottom: 2,
                  }}>
                  {t('backToList')}
                </Typography>
              </Button>
            )}
          </CmtCardHeader>
          <CmtCardContent>
            <GridContainer>
              <GridItem xs={12} md={6}>
                <Box className={classes.deliverySection}>
                  <Typography variant="h1" sx={{ mb: 4 }}>
                    {t('contactInformation')}
                  </Typography>
                  <Box sx={{ mb: 6 }}>
                    <ContactInfoCheckoutSection
                      cart={cart}
                      isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                      validator={validator}
                      isDisabled={cart?.status !== CartStatusEnumValue.PendingDealerApproval && cartId}
                    />
                  </Box>
                  <Typography variant="h1">{t('delivery')}</Typography>

                  <DeliveryCheckoutSection
                    cart={cart}
                    isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                    validator={validator}
                    isDisabled={cart?.status !== CartStatusEnumValue.PendingDealerApproval && cartId}
                    actionBtns={
                      cart?.status === CartStatusEnumValue.PendingDealerApproval ||
                      cart?.status === CartStatusEnumValue.Active
                        ? [
                            {
                              variant: 'contained',
                              color: 'secondary',
                              action:
                                cartId && cart?.status === CartStatusEnumValue.PendingDealerApproval
                                  ? () => setShowApprouveModal(true)
                                  : handleCheckout,
                              label:
                                cartId && cart?.status === CartStatusEnumValue.PendingDealerApproval
                                  ? t('approveCart')
                                  : t('proceedCheckout'),
                            },
                            {
                              variant: 'contained',
                              color: 'error',
                              hidden: !cartId,
                              action: () => setShowCancelModal(true),
                              label: t('cancel'),
                            },
                          ]
                        : []
                    }
                  />
                </Box>
              </GridItem>
              <GridItem xs={12} md={6}>
                <Box>
                  <Typography variant="h1">{t('orderInfo')}</Typography>
                  <OrderInfoCheckoutSection
                    cart={cart}
                    isLoading={gettingCart || gettingCartHistory || isCheckingOut}
                    isDisabled={cartId}
                  />
                </Box>
              </GridItem>
            </GridContainer>
            <Hidden mdUp>
              <Button
                className={classes.checkoutBtn}
                sx={{ borderRadius: 0 }}
                variant="contained"
                color="secondary"
                disabled={!cart || (cartId && cart?.status !== CartStatusEnumValue.PendingDealerApproval) || cart.isDemo}
                onClick={
                  cartId && cart?.status === CartStatusEnumValue.PendingDealerApproval
                    ? () => setShowApprouveModal(true)
                    : handleCheckout
                }>
                <h1>
                  {cartId && cart?.status === CartStatusEnumValue.PendingDealerApproval
                    ? t('approveCart')
                    : t('proceedCheckout')}
                </h1>
              </Button>
            </Hidden>
          </CmtCardContent>
        </CmtCard>
      </Box>
    </>
  );
};

export default Checkout;
